import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bug, CheckCircle, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";

export default function BugReport() {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [userEmail, setUserEmail] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    severity: "",
    steps_to_reproduce: "",
    expected_behavior: "",
    actual_behavior: "",
    page_url: window.location.href,
    browser: navigator.userAgent,
    os: navigator.platform,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login?redirect=/bug-report");
          return;
        }
        setUserEmail(session.user.email || "");
      } catch (error) {
        console.error("Auth check error:", error);
        navigate("/login?redirect=/bug-report");
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSeverityChange = (value: string) => {
    setFormData({
      ...formData,
      severity: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      await api.submitForm("bug_report", {
        ...formData,
        email: userEmail,
      });

      setSubmitStatus("success");
      setFormData({
        title: "",
        description: "",
        severity: "",
        steps_to_reproduce: "",
        expected_behavior: "",
        actual_behavior: "",
        page_url: window.location.href,
        browser: navigator.userAgent,
        os: navigator.platform,
      });
    } catch (error: any) {
      console.error("Bug report error:", error);
      setSubmitStatus("error");
      setErrorMessage(
        error.message || "Failed to submit bug report. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitStatus === "success") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Bug Report Submitted!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Thank you for helping us improve Ithopu. Our team will investigate
              this issue and keep you updated.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => setSubmitStatus("idle")}
                className="w-full"
              >
                Report Another Bug
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="w-full"
              >
                Back to App
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-4">
            <Bug className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Report a Bug
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Found something that's not working right? Let us know so we can fix
            it!
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <p className="font-semibold mb-2">Before reporting a bug:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Try refreshing the page to see if the issue persists</li>
                  <li>Check if others are experiencing the same issue</li>
                  <li>
                    Provide as much detail as possible to help us reproduce the
                    bug
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bug Report Form */}
        <Card>
          <CardHeader>
            <CardTitle>Bug Details</CardTitle>
            <CardDescription>
              Please provide detailed information about the bug you encountered.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  Bug Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  name="title"
                  type="text"
                  placeholder="Brief description of the bug (e.g., 'Login button not working')"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">
                  {formData.title.length}/200 characters
                </p>
              </div>

              {/* Severity */}
              <div className="space-y-2">
                <Label htmlFor="severity">
                  Severity <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.severity}
                  onValueChange={handleSeverityChange}
                  required
                >
                  <SelectTrigger id="severity">
                    <SelectValue placeholder="How severe is this bug?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      Low - Minor inconvenience
                    </SelectItem>
                    <SelectItem value="medium">
                      Medium - Affects functionality
                    </SelectItem>
                    <SelectItem value="high">
                      High - Major feature broken
                    </SelectItem>
                    <SelectItem value="critical">
                      Critical - App unusable
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">
                  Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe what happened and what you were trying to do..."
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={5000}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">
                  {formData.description.length}/5000 characters
                </p>
              </div>

              {/* Steps to Reproduce */}
              <div className="space-y-2">
                <Label htmlFor="steps_to_reproduce">
                  Steps to Reproduce (Optional)
                </Label>
                <Textarea
                  id="steps_to_reproduce"
                  name="steps_to_reproduce"
                  placeholder="1. Go to... &#10;2. Click on...&#10;3. See error..."
                  value={formData.steps_to_reproduce}
                  onChange={handleChange}
                  rows={4}
                  maxLength={5000}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">
                  Help us reproduce the bug by listing the exact steps
                </p>
              </div>

              {/* Expected vs Actual Behavior */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="expected_behavior">
                    Expected Behavior (Optional)
                  </Label>
                  <Textarea
                    id="expected_behavior"
                    name="expected_behavior"
                    placeholder="What should have happened?"
                    value={formData.expected_behavior}
                    onChange={handleChange}
                    rows={3}
                    maxLength={2000}
                    className="resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="actual_behavior">
                    Actual Behavior (Optional)
                  </Label>
                  <Textarea
                    id="actual_behavior"
                    name="actual_behavior"
                    placeholder="What actually happened?"
                    value={formData.actual_behavior}
                    onChange={handleChange}
                    rows={3}
                    maxLength={2000}
                    className="resize-none"
                  />
                </div>
              </div>

              {/* Technical Info (read-only, auto-collected) */}
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Technical Information (automatically collected)
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <Label className="text-gray-600 dark:text-gray-400">
                      Page URL
                    </Label>
                    <p className="text-gray-800 dark:text-gray-200 break-all">
                      {formData.page_url}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-600 dark:text-gray-400">
                      Operating System
                    </Label>
                    <p className="text-gray-800 dark:text-gray-200">
                      {formData.os}
                    </p>
                  </div>
                </div>
              </div>

              {submitStatus === "error" && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="mr-2">Submitting...</span>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </>
                ) : (
                  <>
                    <Bug className="w-4 h-4 mr-2" />
                    Submit Bug Report
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
